import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  IconChevronRight,
  IconEdit,
  IconInfoCircle,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useAddressStore } from '@/src/modules/auth/stores/use-address-store';
import { useAuthStore } from '@/src/modules/auth/stores/use-auth-store';
import { useDeleteProfileEntity } from '@/src/modules/user/hooks/use-delete-profile-entity';
import { MAddress } from '@/src/modules/user/models/address';

interface CartAddressProps {
  addressList?: MAddress[];
  onSelectAddress?: (addressId: number) => void;
  withBorder?: boolean;
  showAlert?: boolean;
  onAddAddress?: () => void;
}

export function CartAddress({
  addressList: propAddressList,
  onSelectAddress,
  withBorder = true,
  showAlert = true,
  onAddAddress,
}: CartAddressProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const router = useRouter();
  const [tempSelected, setTempSelected] = useState<MAddress | null>(null);
  const user = useAuthStore((s) => s.user);
  const individual = useAuthStore((s) => s.individual);
  const storeAddressListRaw = useAddressStore((s) => s.address);
  const storeAddressList = useMemo(
    () => storeAddressListRaw.filter((a) => a.is_active),
    [storeAddressListRaw]
  );
  const setUserAddress = useAddressStore((s) => s.setSelectedAddress);
  const deleteStoreAddress = useAddressStore((s) => s.deleteAddress);
  const selectedUserAddress = useAddressStore((s) => s.selectedAddress);
  const { trigger: deleteProfileEntity } = useDeleteProfileEntity();

  const effectiveAddressList = useMemo(() => {
    const list =
      propAddressList && propAddressList.length > 0
        ? [...propAddressList]
        : [...storeAddressList];

    return list.sort((a, b) => {
      if (a.is_main_address && !b.is_main_address) {
        return -1;
      }
      if (!a.is_main_address && b.is_main_address) {
        return 1;
      }
      return 0;
    });
  }, [propAddressList, storeAddressList]);
  const hasAddress = effectiveAddressList.length > 0;
  const displayAddress = useMemo(() => {
    if (selectedUserAddress) {
      return selectedUserAddress;
    }

    if (effectiveAddressList && effectiveAddressList.length > 0) {
      return (
        effectiveAddressList.find((a) => a.is_main_address) ||
        effectiveAddressList[0]
      );
    }

    return null;
  }, [selectedUserAddress, effectiveAddressList]);

  useEffect(() => {
    if (displayAddress) {
      onSelectAddress?.(displayAddress.id);
    }
  }, [displayAddress, onSelectAddress]);

  return (
    <Paper
      radius="md"
      withBorder={withBorder}
      flex={1}
      className={`cursor-pointer transition-all hover:shadow-md ${
        withBorder ? 'border-gray-200' : ''
      }`}
    >
      {hasAddress ? (
        <Paper
          p="md"
          radius="md"
          withBorder
          mb="md"
          flex={1}
          className="cursor-pointer border-gray-200 transition-all hover:shadow-md"
          onClick={() => {
            if (!hasAddress) {
              return;
            }
            setTempSelected(displayAddress);
            open();
          }}
        >
          <Group align="center">
            <div className="flex-1">
              {displayAddress?.is_main_address && (
                <Badge variant="filled" size="sm" radius="sm" mb={4}>
                  Utama
                </Badge>
              )}

              <Group gap="xs" mb={2}>
                <Text fw={600} size="md" className="text-gray-800">
                  {displayAddress ? (
                    <>
                      {displayAddress.place} - {displayAddress.phone_number}
                    </>
                  ) : (
                    '-'
                  )}
                </Text>
              </Group>

              <Text size="sm" c="dimmed" lineClamp={2}>
                {displayAddress?.body_address || ''}
              </Text>
            </div>

            <IconChevronRight size={20} className="mt-2 text-gray-400" />
          </Group>

          {showAlert && (
            <Alert
              mt={16}
              variant="light"
              color="blue"
              icon={<IconInfoCircle />}
              title="Perhatian"
            >
              Alamat akan digunakan untuk pengiriman barang maupun lokasi
              Homecare. Jika anda memilih layanan tatap muka atau online, bisa
              abaikan alamat ini.
            </Alert>
          )}
        </Paper>
      ) : (
        <Text>
          Silahkan tambahkan alamat untuk dapat memilih alamat pengiriman
        </Text>
      )}
      <Button
        variant="outline"
        color="brand"
        leftSection={<IconPlus size={18} />}
        radius="md"
        fullWidth
        mt="md"
        onClick={() => {
          if (onAddAddress) {
            onAddAddress();
          }
          router.push(`/address`);
        }}
      >
        Tambah Alamat
      </Button>
      {/* POPUP / MODAL */}
      <Modal
        opened={opened}
        onClose={close}
        title="Pilih Alamat Pengiriman"
        centered
        size="lg"
      >
        <Stack>
          <div className="flex h-[calc(100vh-300px)] flex-col gap-4 overflow-y-auto">
            {effectiveAddressList.map((item) => {
              const isSelected = tempSelected?.id === item.id;

              return (
                <Paper
                  key={item.id}
                  withBorder
                  p="md"
                  radius="md"
                  onClick={() => setTempSelected(item)}
                  style={{
                    cursor: 'pointer',
                    borderColor: isSelected
                      ? 'var(--mantine-color-blue-5)'
                      : undefined,
                    backgroundColor: isSelected
                      ? 'var(--mantine-color-blue-0)'
                      : undefined,
                  }}
                  className="hover:bg-gray-50"
                >
                  {item.is_main_address && (
                    <Badge size="sm" mr={6}>
                      Utama
                    </Badge>
                  )}

                  <Text>
                    {item.place}{' '}
                    {item.phone_number ? `— ${item.phone_number}` : ''}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {item.body_address}
                  </Text>
                  <Group justify="flex-end">
                    <ActionIcon
                      variant="subtle"
                      aria-label="Settings"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onAddAddress) {
                          onAddAddress();
                        }
                        close();
                        router.push(`/address?id=${item.id}`);
                      }}
                    >
                      <IconEdit
                        style={{ width: '70%', height: '70%' }}
                        stroke={1.5}
                      />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      aria-label="Settings"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!user?.props.id) {
                          return;
                        }
                        deleteProfileEntity({
                          id: item.id,
                          userId: user?.props.id,
                          model: 'MemberAddress',
                        });
                        deleteStoreAddress(item.id);
                        close();
                      }}
                    >
                      <IconTrash
                        style={{ width: '70%', height: '70%' }}
                        stroke={1.5}
                      />
                    </ActionIcon>
                  </Group>
                </Paper>
              );
            })}

            {effectiveAddressList.length === 0 && (
              <Text c="dimmed" ta="center">
                Tidak ada alamat tersedia
              </Text>
            )}
          </div>

          {/* Dynamic Button */}
          <Button
            fullWidth
            onClick={() => {
              if (tempSelected) {
                setUserAddress(
                  tempSelected.id,
                  individual?.props.id,
                  user?.props.id
                );
                onSelectAddress?.(tempSelected.id);
              }
              close();
            }}
          >
            {displayAddress && tempSelected?.id === displayAddress.id
              ? 'Tutup'
              : 'Pilih Alamat'}
          </Button>
        </Stack>
      </Modal>
    </Paper>
  );
}
